import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { GameConfig } from '../config/GameConfig';

export class ScratchingPostWeapon {
  private scene: Phaser.Scene;
  private player: Player;
  private enemies: Phaser.Physics.Arcade.Group;
  private posts: Phaser.GameObjects.Graphics[] = [];
  private damageMultiplier: number = 1;
  private postCount: number = 2;
  private orbitRadius: number = GameConfig.WEAPON_DATA.SCRATCHING_POST.ORBIT_RADIUS;
  private rotationSpeed: number = GameConfig.WEAPON_DATA.SCRATCHING_POST.ROTATION_SPEED;
  private currentAngle: number = 0;
  private damageTimer?: Phaser.Time.TimerEvent;

  constructor(scene: Phaser.Scene, player: Player, enemies: Phaser.Physics.Arcade.Group) {
    this.scene = scene;
    this.player = player;
    this.enemies = enemies;

    this.createPosts();
    this.startDamaging();
    this.startUpdating();
  }

  private createPosts(): void {
    for (let i = 0; i < this.postCount; i++) {
      const post = this.scene.add.graphics();
      post.setDepth(6);
      this.drawPost(post);
      this.posts.push(post);
    }
  }

  private drawPost(graphics: Phaser.GameObjects.Graphics): void {
    // Scratching post appearance
    // Base
    graphics.fillStyle(0x8B4513, 1);
    graphics.fillRect(-8, -12, 16, 24);

    // Rope texture
    graphics.lineStyle(2, 0xD2691E, 0.8);
    for (let i = 0; i < 6; i++) {
      graphics.lineBetween(-8, -12 + i * 4, 8, -10 + i * 4);
    }

    // Scratches
    graphics.lineStyle(1, 0x654321, 1);
    graphics.lineBetween(-5, -8, -2, 0);
    graphics.lineBetween(2, -4, 5, 4);
    graphics.lineBetween(-3, 2, 0, 8);
  }

  private startUpdating(): void {
    this.scene.events.on('update', this.update, this);
  }

  private update(): void {
    if (!this.player.active) return;

    this.currentAngle += this.rotationSpeed;

    // Update post positions to orbit around player
    for (let i = 0; i < this.posts.length; i++) {
      const angle = this.currentAngle + (i * (Math.PI * 2) / this.posts.length);
      const x = this.player.x + Math.cos(angle) * this.orbitRadius;
      const y = this.player.y + Math.sin(angle) * this.orbitRadius;

      this.posts[i].setPosition(x, y);
      this.posts[i].setRotation(angle + Math.PI / 2);
    }
  }

  private startDamaging(): void {
    this.damageTimer = this.scene.time.addEvent({
      delay: GameConfig.WEAPON_DATA.SCRATCHING_POST.TICK_RATE, // Check for collisions 5 times per second
      callback: () => {
        if (!this.player.active) return;

        const enemyArray = this.enemies.getChildren() as Enemy[];

        // Check each post for collisions
        this.posts.forEach(post => {
          enemyArray.forEach(enemy => {
            if (!enemy.active) return;

            const distance = Phaser.Math.Distance.Between(
              post.x, post.y,
              enemy.x, enemy.y
            );

            if (distance < 20) {
              enemy.takeDamage(GameConfig.WEAPON_DATA.SCRATCHING_POST.DAMAGE * this.damageMultiplier);
            }
          });
        });
      },
      callbackScope: this,
      loop: true
    });
  }

  public updateDamageMultiplier(multiplier: number): void {
    this.damageMultiplier = multiplier;
  }

  public upgradeDamage(amount: number): void {
    this.damageMultiplier += amount;
  }

  public upgradeSpeed(amount: number): void {
    this.rotationSpeed += amount * 0.01;
  }

  public addPost(): void {
    const post = this.scene.add.graphics();
    post.setDepth(6);
    this.drawPost(post);
    this.posts.push(post);
    this.postCount++;
  }

  public upgradeRadius(amount: number): void {
    this.orbitRadius += amount;
  }

  public destroy(): void {
    if (this.damageTimer) {
      this.damageTimer.destroy();
    }
    this.scene.events.off('update', this.update, this);
    this.posts.forEach(post => {
      post.clear();
      post.destroy();
    });
    this.posts = [];
  }
}
